import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import Icon from './Icon';
import { getAccessToken, isSecretaryContextUser } from '../authStorage';
import { fetchBranches } from '../branchesApi';
import { fetchDepartments } from '../departmentsApi';
import {
  buildUpsertPayload,
  createOperationalPlan,
  fetchOperationalPlanById,
  mapDetailToForm,
  updateOperationalPlan,
} from '../operationalPlansApi';
import { exportOperationalPlanPDF } from '../operationalPlanPdfExport';
import { useLanguage } from '../i18n/language';
import './BranchesPage.css';
import './DecisionFormPage.css';
import './OperationalPlanFormPage.css';

const nk = (p) => `${p}-${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;

function emptyForm(firstBranchId) {
  return {
    branchId: firstBranchId || '',
    departmentId: '',
    title: '',
    status: 'draft',
    periodStart: '',
    periodEnd: '',
    preparedBy: '',
    approvedBy: '',
    overviewDescription: '',
    overviewPurpose: '',
    vision: '',
    mission: '',
    valuesText: '',
    reviewedBy: '',
    approvalConfirmed: false,
    objectives: [],
    axes: [],
    hrResources: [],
    financialResources: [],
    kpis: [],
    risks: [],
  };
}

/** Demo payload for previews and testing saves (Arabic-first copy). */
function buildSampleOperationalPlanForm(branchId, deptIdStr, isAr) {
  const d = deptIdStr != null && deptIdStr !== '' ? String(deptIdStr) : '';
  const y = new Date().getFullYear();
  const L = (ar, en) => (isAr ? ar : en);

  const o0 = {
    _key: nk('o'),
    sortOrder: 0,
    objectiveText: L('رفع كفاءة العمليات وتقليل الهدر', 'Improve efficiency and reduce waste'),
    successIndicator: L('تحسّن مؤشرات الإنتاجية بنسبة ١٠٪', 'Productivity indicators +10%'),
    responsibleDepartmentId: d,
  };
  const o1 = {
    _key: nk('o'),
    sortOrder: 1,
    objectiveText: L('تعزيز تجربة المستفيدين', 'Enhance stakeholder experience'),
    successIndicator: L('رضا المستفيدين لا يقل عن ٨٥٪', 'Stakeholder satisfaction ≥ 85%'),
    responsibleDepartmentId: d,
  };

  const mkAct = (sortOrder, name, description, prog, status = 'planned') => ({
    _key: nk('a'),
    sortOrder,
    name,
    description,
    startDate: `${y}-02-01`,
    endDate: `${y}-08-31`,
    responsiblePersonName: L('م. خالد الأحمد', 'K. Al Ahmad'),
    responsibleDepartmentId: d,
    status,
    progressPercentage: prog,
  });

  const axis = {
    _key: nk('x'),
    sortOrder: 0,
    name: L('محور التحسين المستمر', 'Continuous improvement'),
    departmentId: d,
    activities: [
      mkAct(0, L('تدقيق العمليات الداخلية', 'Internal process audit'), L('مراجعات شهرية وفق قائمة تحقق', 'Monthly reviews'), 35, 'in_progress'),
      mkAct(1, L('تدريب الفرق', 'Team training'), L('٢٠ ساعة تدريبية لكل موظف', '20 hours per FTE'), 20, 'planned'),
    ],
  };

  return {
    branchId: branchId || '',
    departmentId: d,
    title: L(`خطة تشغيلية ${y} — نموذج تجريبي`, `Operational plan ${y} — sample`),
    status: 'under_review',
    periodStart: `${y}-01-01`,
    periodEnd: `${y}-12-31`,
    preparedBy: L('مكتب التخطيط والمتابعة', 'Planning office'),
    approvedBy: L('الرئيس التنفيذي', 'CEO'),
    overviewDescription: L(
      'تهدف هذه الخطة إلى ترسيخ الأولويات التشغيلية للفترة وربط الموارد بالنتائج المتوقعة.',
      'This sample aligns operational priorities with expected outcomes.'
    ),
    overviewPurpose: L('توحيد الجهود وقياس الأثر بشكل دوري.', 'Align efforts and measure impact periodically.'),
    vision: L('ريادة الخدمات بكفاءة وشفافية.', 'Leading services with efficiency and transparency.'),
    mission: L('تمكين الفرق وتحسين العمليات باستمرار.', 'Empower teams and improve processes continuously.'),
    valuesText: L('الالتزام — الجودة — التعاون — الابتكار', 'Commitment — Quality — Collaboration — Innovation'),
    reviewedBy: L('مسؤول الامتثال', 'Compliance lead'),
    approvalConfirmed: false,
    objectives: [o0, o1],
    axes: [axis],
    hrResources: [{
      _key: nk('h'),
      sortOrder: 0,
      role: L('منسق مشاريع', 'Project coordinator'),
      numberRequired: '2',
      natureOfWork: L('متابعة الخطة وإعداد التقارير', 'Plan tracking and reports'),
      notes: L('دوام كامل', 'Full-time'),
    }],
    financialResources: [{
      _key: nk('f'),
      sortOrder: 0,
      budgetItem: L('برمجيات وتدريب', 'Software & training'),
      estimatedAmount: '120000',
      actualAmount: '45000',
      notes: L('توزيع ربعي', 'Quarterly drawdown'),
    }],
    kpis: [{
      _key: nk('k'),
      sortOrder: 0,
      relatedObjectiveSortOrder: '0',
      name: L('عدد المبادرات المكتملة', 'Completed initiatives'),
      targetValue: L('١٢ مبادرة', '12 initiatives'),
      responsibleDepartmentId: d,
      quarter1: '3',
      quarter2: '4',
      quarter3: '5',
      measurementMethod: L('سجل مشاريع معتمد', 'Approved project log'),
    }],
    risks: [{
      _key: nk('r'),
      sortOrder: 0,
      description: L('تأخير اعتماد الميزانية', 'Budget approval delay'),
      probability: L('متوسط', 'Medium'),
      impact: L('عالٍ', 'High'),
      mitigationPlan: L('خطط تشغيل مؤقتة وتمويل مرحلي', 'Interim operational plan and phased funding'),
    }],
  };
}

function DeptSelect({ value, onChange, options, disabled }) {
  return (
    <select className="op-in mform__select" value={value} onChange={(e) => onChange(e.target.value)} disabled={disabled}>
      <option value="">—</option>
      {options.map((d) => <option key={d.id} value={String(d.id)}>{d.parentName ? `${d.parentName} › ${d.name}` : d.name}</option>)}
    </select>
  );
}

export default function OperationalPlanFormPage({ mode }) {
  const { language } = useLanguage();
  const isAr = language === 'ar';
  const t = useMemo(() => ({
    noAccess: isAr ? 'لا تملك صلاحية عرض هذه الصفحة.' : 'No access.',
    invalidId: isAr ? 'معرّف غير صالح' : 'Invalid id',
    loadFailed: isAr ? 'فشل التحميل' : 'Load failed',
    saveFailed: isAr ? 'فشل الحفظ' : 'Save failed',
    cancel: isAr ? 'إلغاء' : 'Cancel',
    saving: isAr ? 'جارٍ الحفظ…' : 'Saving…',
    loading: isAr ? 'جارٍ التحميل…' : 'Loading…',
    newTitle: isAr ? 'خطة تشغيلية جديدة' : 'New operational plan',
    editTitle: isAr ? 'تعديل الخطة التشغيلية' : 'Edit operational plan',
    subtitle: isAr ? 'البيانات والأهداف والمحاور والموارد وفق مستند احترافي' : 'Structured document: overview, objectives, axes, resources',
    save: isAr ? 'حفظ الخطة' : 'Save plan',
    exportPdf: isAr ? 'تصدير PDF' : 'Export PDF',
    exportingPdf: isAr ? 'جارٍ تصدير PDF…' : 'Exporting PDF…',
    basic: isAr ? 'أولاً: المعلومات الأساسية' : 'Basic information',
    overview: isAr ? 'ثانياً: نظرة عامة' : 'Plan overview',
    vmv: isAr ? 'الرؤية والرسالة والقيم' : 'Vision / mission / values',
    objs: isAr ? 'الأهداف التشغيلية' : 'Operational objectives',
    axes: isAr ? 'خطة العمل والمحاور' : 'Axes and activities',
    hr: isAr ? 'الموارد البشرية' : 'Human resources',
    fin: isAr ? 'الموارد المالية' : 'Financial resources',
    kpiSec: isAr ? 'مؤشرات الأداء' : 'KPIs',
    riskSec: isAr ? 'المخاطر' : 'Risks',
    appr: isAr ? 'الاعتماد' : 'Approval',
    lblTitle: isAr ? 'عنوان الخطة' : 'Plan title',
    lblBranch: isAr ? 'الجهة / الشركة' : 'Organization / company',
    lblDept: isAr ? 'الإدارة المعنية بالمستند' : 'Responsible department',
    lblPeriodStart: isAr ? 'بداية الفترة' : 'Period start',
    lblPeriodEnd: isAr ? 'نهاية الفترة' : 'Period end',
    lblPrepared: isAr ? 'معد الخطة' : 'Prepared by',
    lblApproved: isAr ? 'المعتمد من' : 'Approved by',
    lblStatus: isAr ? 'الحالة' : 'Status',
    lblDesc: isAr ? 'وصف عام' : 'Description',
    lblPurpose: isAr ? 'الغرض / الأهمية' : 'Purpose',
    lblVision: isAr ? 'الرؤية' : 'Vision',
    lblMission: isAr ? 'الرسالة' : 'Mission',
    lblValues: isAr ? 'القيم' : 'Values',
    lblRev: isAr ? 'المراجع' : 'Reviewer',
    lblConfirmed: isAr ? 'تأكيد الاعتماد' : 'Confirmation recorded',
    addRow: isAr ? 'إضافة صف' : 'Add row',
    rem: isAr ? 'حذف' : 'Remove',
    addAxis: isAr ? 'إضافة محور' : 'Add axis',
    addAct: isAr ? 'إضافة نشاط' : 'Add activity',
    fillSample: isAr ? 'تعبئة بيانات تجريبية' : 'Fill sample data',
    phTitle: isAr ? 'مثال: الخطة التشغيلية السنوية' : 'e.g. Annual operational plan',
    phPrepared: isAr ? 'اسم معد الخطة أو الجهة' : 'Preparer name or unit',
    phApproved: isAr ? 'اسم المعتمِد أو المنصب' : 'Approver name or role',
    phOverview: isAr ? 'نبذة عن السياق وأهم أولويات هذه الخطة…' : 'Context and key priorities…',
    phPurpose: isAr ? 'لماذا هذه الخطة مهمة الآن؟' : 'Why this plan matters…',
    phVision: isAr ? 'أين تريد المؤسسة أن تكون في الأفق الزمني للخطة؟' : 'Where the org aims to be…',
    phMission: isAr ? 'ما الذي تفعله يوميًا لخدمة المستفيدين؟' : 'What you do day to day…',
    phValues: isAr ? 'قيم تلتزم بها الفرق (مثال: الشفافية، الجودة)…' : 'Values (e.g. transparency, quality)…',
    phObjective: isAr ? 'صياغة الهدف بوضوح' : 'State the objective clearly',
    phSuccessInd: isAr ? 'كيف تعرف أن الهدف تحقق؟' : 'How you know it is achieved',
    phAxisName: isAr ? 'مثال: التحول الرقمي' : 'e.g. Digital transformation',
    phActivity: isAr ? 'اسم النشاط' : 'Activity name',
    phActDesc: isAr ? 'تفاصيل التنفيذ' : 'Implementation details',
    phPerson: isAr ? 'مسؤول تنفيذ النشاط' : 'Responsible person',
    phHrRole: isAr ? 'مثال: محلل بيانات' : 'e.g. Data analyst',
    phHrNum: isAr ? 'عدد' : 'Headcount',
    phHrNature: isAr ? 'وصف مختصر للمهام' : 'Brief duties',
    phHrNotes: isAr ? 'ملاحظات اختيارية' : 'Optional notes',
    phBudgetItem: isAr ? 'مثال: تدريب / تراخيص' : 'e.g. Training / licenses',
    phAmount: isAr ? '0' : '0',
    phFinNotes: isAr ? 'ربط بالميزانية أو التعاقدات' : 'Budget / procurement notes',
    phKpiName: isAr ? 'مؤشر قابل للقياس' : 'Measurable KPI',
    phKpiTarget: isAr ? 'مثال: ٩٥٪' : 'e.g. 95%',
    phKpiMeas: isAr ? 'طريقة الجمع والتقرير' : 'Collection & reporting method',
    phRiskDesc: isAr ? 'وصف المخاطر التي قد تعيق التنفيذ' : 'Describe the risk',
    phRiskProb: isAr ? 'منخفض / متوسط / عالٍ' : 'Low / medium / high',
    phRiskImpact: isAr ? 'تأثير محتمل على الجدول أو الجودة' : 'Schedule or quality impact',
    phRiskMit: isAr ? 'إجراءات التخفيف أو البدائل' : 'Mitigation or alternatives',
    phRev: isAr ? 'اسم المراجع' : 'Reviewer name',
  }), [isAr]);

  const navigate = useNavigate();
  const location = useLocation();
  const basePath = location.pathname.startsWith('/officer') ? '/officer' : '/app';
  const { id: idParam } = useParams();
  const isNew = mode === 'new';
  const editId = isNew ? null : Number(idParam);
  const invalidEditId = !isNew && (!Number.isFinite(editId) || editId < 1);

  const [branches, setBranches] = useState([]);
  const [departmentsFromApi, setDepartmentsFromApi] = useState([]);
  const [form, setForm] = useState(() => emptyForm());
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [apiErr, setApiErr] = useState('');
  const [exportingPdf, setExportingPdf] = useState(false);

  const scopedDepts = useMemo(() => {
    const bid = form.branchId;
    if (!bid) return [];
    return departmentsFromApi.filter((d) => String(d.branchId) === String(bid));
  }, [departmentsFromApi, form.branchId]);

  useEffect(() => {
    if (!getAccessToken()) { navigate('/login', { replace: true }); return; }
    if (!isSecretaryContextUser()) { setLoadError(t.noAccess); setLoading(false); return; }
    if (invalidEditId) { setLoadError(t.invalidId); setLoading(false); return; }
    let cancelled = false;
    (async () => {
      setLoading(true);
      setLoadError('');
      try {
        const [brRows, depRows] = await Promise.all([fetchBranches(), fetchDepartments()]);
        if (cancelled) return;
        setBranches(brRows);
        setDepartmentsFromApi(depRows);
        const fb = brRows[0]?.id ? String(brRows[0].id) : '';
        if (isNew) setForm(emptyForm(fb));
        else {
          const raw = await fetchOperationalPlanById(editId);
          if (cancelled) return;
          setForm(mapDetailToForm(raw));
        }
      } catch (e) {
        if (!cancelled) setLoadError(e instanceof Error ? e.message : t.loadFailed);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [navigate, isNew, editId, invalidEditId, t.noAccess, t.invalidId, t.loadFailed]);

  const planStatuses = useMemo(() => ([
    { v: 'draft', l: isAr ? 'مسودة' : 'Draft' },
    { v: 'under_review', l: isAr ? 'قيد المراجعة' : 'Under review' },
    { v: 'approved', l: isAr ? 'معتمدة' : 'Approved' },
    { v: 'in_progress', l: isAr ? 'جاري التنفيذ' : 'In progress' },
    { v: 'completed', l: isAr ? 'مكتملة' : 'Completed' },
  ]), [isAr]);
  const actStatuses = useMemo(() => ([
    { v: 'planned', l: isAr ? 'مخطط' : 'Planned' },
    { v: 'in_progress', l: isAr ? 'جاري' : 'In progress' },
    { v: 'done', l: isAr ? 'منجز' : 'Done' },
    { v: 'paused', l: isAr ? 'موقوف' : 'Paused' },
    { v: 'cancelled', l: isAr ? 'ملغى' : 'Cancelled' },
  ]), [isAr]);

  const update = (patch) => setForm((p) => ({ ...p, ...patch }));

  const addObjective = () => {
    const next = Math.max(-1, ...form.objectives.map((o) => o.sortOrder)) + 1;
    update({ objectives: [...form.objectives, { _key: nk('o'), sortOrder: next, objectiveText: '', successIndicator: '', responsibleDepartmentId: '' }] });
  };

  const addAxis = () => {
    const next = Math.max(-1, ...form.axes.map((x) => x.sortOrder)) + 1;
    update({ axes: [...form.axes, { _key: nk('x'), sortOrder: next, name: '', departmentId: '', activities: [] }] });
  };

  const addActivity = (xi) => {
    const axes = [...form.axes];
    const acts = [...(axes[xi].activities || [])];
    const next = Math.max(-1, ...acts.map((a) => a.sortOrder)) + 1;
    acts.push({ _key: nk('a'), sortOrder: next, name: '', description: '', startDate: '', endDate: '', responsiblePersonName: '', responsibleDepartmentId: '', status: 'planned', progressPercentage: 0 });
    axes[xi] = { ...axes[xi], activities: acts };
    update({ axes });
  };

  const addHr = () => {
    const next = Math.max(-1, ...form.hrResources.map((x) => x.sortOrder)) + 1;
    update({ hrResources: [...form.hrResources, { _key: nk('h'), sortOrder: next, role: '', numberRequired: '', natureOfWork: '', notes: '' }] });
  };

  const addFin = () => {
    const next = Math.max(-1, ...form.financialResources.map((x) => x.sortOrder)) + 1;
    update({ financialResources: [...form.financialResources, { _key: nk('f'), sortOrder: next, budgetItem: '', estimatedAmount: '', actualAmount: '', notes: '' }] });
  };

  const addKpi = () => {
    const next = Math.max(-1, ...form.kpis.map((x) => x.sortOrder)) + 1;
    update({ kpis: [...form.kpis, { _key: nk('k'), sortOrder: next, relatedObjectiveSortOrder: '', name: '', targetValue: '', responsibleDepartmentId: '', quarter1: '', quarter2: '', quarter3: '', measurementMethod: '' }] });
  };

  const addRisk = () => {
    const next = Math.max(-1, ...form.risks.map((x) => x.sortOrder)) + 1;
    update({ risks: [...form.risks, { _key: nk('r'), sortOrder: next, description: '', probability: '', impact: '', mitigationPlan: '' }] });
  };

  const handleExportPdf = async () => {
    if (!editId) return;
    setExportingPdf(true);
    setApiErr('');
    try {
      const detail = await fetchOperationalPlanById(editId);
      await exportOperationalPlanPDF(detail);
    } catch (e) {
      setApiErr(e instanceof Error ? e.message : (isAr ? 'فشل تصدير PDF' : 'PDF export failed'));
    } finally {
      setExportingPdf(false);
    }
  };

  const fillSampleOperationalPlan = () => {
    const bid = form.branchId || (branches[0]?.id != null ? String(branches[0].id) : '');
    const depts = bid ? departmentsFromApi.filter((x) => String(x.branchId) === String(bid)) : [];
    const did = depts[0]?.id != null ? String(depts[0].id) : '';
    setForm(buildSampleOperationalPlanForm(bid, did, isAr));
    setApiErr('');
  };

  const handleSave = async () => {
    if (!String(form.branchId || '').trim() || !(form.title || '').trim()) {
      setApiErr(isAr ? 'الشركة والعنوان مطلوبان.' : 'Company and title are required.');
      return;
    }
    setApiErr('');
    setSaving(true);
    try {
      const payload = buildUpsertPayload(form);
      if (isNew) await createOperationalPlan(payload);
      else await updateOperationalPlan(editId, payload);
      navigate(`${basePath}/operational-plans`);
    } catch (e) {
      setApiErr(e instanceof Error ? e.message : t.saveFailed);
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="decision-v2" dir={isAr ? 'rtl' : 'ltr'}><div className="page-wrapper"><div className="v2-loading">{t.loading}</div></div></div>;

  return (
    <div className="decision-v2 op-plan decision-v2--layout-full" dir={isAr ? 'rtl' : 'ltr'}>
      <div className="page-wrapper">
        {loadError && <div className="v2-error">{loadError}</div>}
        {!loadError && (
          <div className="card" style={{ borderRadius: 12, overflow: 'hidden' }}>
            <div className="op-plan__banner">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                <div>
                  <h1>{isNew ? t.newTitle : t.editTitle}</h1>
                  <p>{t.subtitle}</p>
                </div>
                <div className="action-btns" style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
                  <button type="button" className="btn btn-ghost" style={{ background: 'transparent', borderColor: 'rgba(255,255,255,0.35)', color: '#fff' }} onClick={fillSampleOperationalPlan} disabled={exportingPdf || saving}>{t.fillSample}</button>
                  {!isNew && (
                    <button type="button" className="btn btn-ghost" style={{ background: 'transparent', borderColor: 'rgba(255,255,255,0.35)', color: '#fff' }} onClick={handleExportPdf} disabled={exportingPdf || saving}>
                      {exportingPdf ? t.exportingPdf : t.exportPdf}
                    </button>
                  )}
                  <button type="button" className="btn btn-ghost" style={{ background: 'transparent', borderColor: 'rgba(255,255,255,0.35)', color: '#fff' }} onClick={() => navigate(`${basePath}/operational-plans`)}>{t.cancel}</button>
                  <button type="button" className="btn btn-primary" onClick={handleSave} disabled={saving || exportingPdf}>{saving ? t.saving : t.save}</button>
                </div>
              </div>
            </div>

            <div className="card-inner" style={{ padding: 16 }}>
              {apiErr && <div className="v2-error" style={{ marginBottom: 12 }}>{apiErr}</div>}

              <div className="op-plan__sec">
                <div className="op-plan__sec-head">{t.basic}</div>
                <div className="op-plan__sec-body mform">
                  <div className="mform__row mform__row--4">
                    <div className="mform__field mform__field--span2">
                      <label className="mform__label">{t.lblTitle} *</label>
                      <input className="mform__input op-in" value={form.title} onChange={(e) => update({ title: e.target.value })} placeholder={t.phTitle} />
                    </div>
                    <div className="mform__field">
                      <label className="mform__label">{t.lblBranch} *</label>
                      <select className="mform__select" value={form.branchId} onChange={(e) => update({ branchId: e.target.value, departmentId: '' })}>
                        <option value="">—</option>
                        {branches.map((b) => <option key={b.id} value={String(b.id)}>{b.name}</option>)}
                      </select>
                    </div>
                    <div className="mform__field">
                      <label className="mform__label">{t.lblDept}</label>
                      <DeptSelect value={form.departmentId} onChange={(v) => update({ departmentId: v })} options={scopedDepts} disabled={!form.branchId} />
                    </div>
                  </div>
                  <div className="mform__row mform__row--4">
                    <div className="mform__field">
                      <label className="mform__label">{t.lblPeriodStart}</label>
                      <input type="date" className="mform__input" dir="ltr" value={form.periodStart} onChange={(e) => update({ periodStart: e.target.value })} />
                    </div>
                    <div className="mform__field">
                      <label className="mform__label">{t.lblPeriodEnd}</label>
                      <input type="date" className="mform__input" dir="ltr" value={form.periodEnd} onChange={(e) => update({ periodEnd: e.target.value })} />
                    </div>
                    <div className="mform__field">
                      <label className="mform__label">{t.lblPrepared}</label>
                      <input className="mform__input" value={form.preparedBy} onChange={(e) => update({ preparedBy: e.target.value })} placeholder={t.phPrepared} />
                    </div>
                    <div className="mform__field">
                      <label className="mform__label">{t.lblApproved}</label>
                      <input className="mform__input" value={form.approvedBy} onChange={(e) => update({ approvedBy: e.target.value })} placeholder={t.phApproved} />
                    </div>
                  </div>
                  <div className="mform__row mform__row--4">
                    <div className="mform__field mform__field--span4">
                      <label className="mform__label">{t.lblStatus}</label>
                      <select className="mform__select" value={form.status} onChange={(e) => update({ status: e.target.value })}>
                        {planStatuses.map((s) => <option key={s.v} value={s.v}>{s.l}</option>)}
                      </select>
                    </div>
                  </div>
                </div>
              </div>

              <div className="op-plan__sec">
                <div className="op-plan__sec-head">{t.overview}</div>
                <div className="op-plan__sec-body">
                  <div className="mform__field" style={{ marginBottom: 10 }}>
                    <label className="mform__label">{t.lblDesc}</label>
                    <textarea className="mform__textarea op-area" value={form.overviewDescription} onChange={(e) => update({ overviewDescription: e.target.value })} placeholder={t.phOverview} />
                  </div>
                  <div className="mform__field">
                    <label className="mform__label">{t.lblPurpose}</label>
                    <textarea className="mform__textarea op-area" value={form.overviewPurpose} onChange={(e) => update({ overviewPurpose: e.target.value })} placeholder={t.phPurpose} />
                  </div>
                </div>
              </div>

              <div className="op-plan__sec">
                <div className="op-plan__sec-head">{t.vmv}</div>
                <div className="op-plan__sec-body">
                  <div className="mform__field" style={{ marginBottom: 10 }}>
                    <label className="mform__label">{t.lblVision}</label>
                    <textarea className="mform__textarea op-area" value={form.vision} onChange={(e) => update({ vision: e.target.value })} placeholder={t.phVision} />
                  </div>
                  <div className="mform__field" style={{ marginBottom: 10 }}>
                    <label className="mform__label">{t.lblMission}</label>
                    <textarea className="mform__textarea op-area" value={form.mission} onChange={(e) => update({ mission: e.target.value })} placeholder={t.phMission} />
                  </div>
                  <div className="mform__field">
                    <label className="mform__label">{t.lblValues}</label>
                    <textarea className="mform__textarea op-area" value={form.valuesText} onChange={(e) => update({ valuesText: e.target.value })} placeholder={t.phValues} />
                  </div>
                </div>
              </div>

              <div className="op-plan__sec">
                <div className="op-plan__sec-head" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span>{t.objs}</span>
                  <button type="button" className="btn btn-outline" onClick={addObjective}><Icon name="plus" size={12} /> {t.addRow}</button>
                </div>
                <div className="op-plan__sec-body">
                  <table className="op-mini">
                    <thead>
                      <tr>
                        <th>{isAr ? 'الهدف' : 'Objective'}</th>
                        <th>{isAr ? 'مؤشر النجاح' : 'Success indicator'}</th>
                        <th>{isAr ? 'الجهة المسؤولة' : 'Dept'}</th>
                        <th style={{ width: 72 }}>{t.rem}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {form.objectives.length === 0 && (<tr><td colSpan={4} className="op-plan__muted">{isAr ? 'لا توجد أهداف بعد' : 'No objectives yet'}</td></tr>)}
                      {form.objectives.map((row, ri) => (
                        <tr key={row._key}>
                          <td><input className="op-in mform__input" value={row.objectiveText} placeholder={t.phObjective} onChange={(e) => {
                            const o = [...form.objectives]; o[ri] = { ...row, objectiveText: e.target.value }; update({ objectives: o });
                          }}
                          />
                          </td>
                          <td><input className="op-in mform__input" value={row.successIndicator} placeholder={t.phSuccessInd} onChange={(e) => { const o = [...form.objectives]; o[ri] = { ...row, successIndicator: e.target.value }; update({ objectives: o }); }} /></td>
                          <td><DeptSelect value={row.responsibleDepartmentId} options={scopedDepts} disabled={!form.branchId} onChange={(v) => { const o = [...form.objectives]; o[ri] = { ...row, responsibleDepartmentId: v }; update({ objectives: o }); }} /></td>
                          <td><button type="button" className="btn btn-ghost" style={{ padding: '4px 8px' }} onClick={() => update({ objectives: form.objectives.filter((_, i) => i !== ri) })}>{t.rem}</button></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="op-plan__sec">
                <div className="op-plan__sec-head" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span>{t.axes}</span>
                  <button type="button" className="btn btn-outline" onClick={addAxis}><Icon name="plus" size={12} /> {t.addAxis}</button>
                </div>
                <div className="op-plan__sec-body">
                  {form.axes.length === 0 ? (<p className="op-plan__muted">{isAr ? 'لا توجد محاور' : 'No axes yet'}</p>) : form.axes.map((ax, xi) => (
                    <div key={ax._key} className="op-plan__nested" style={{ marginBottom: 12 }}>
                      <div className="mform__row mform__row--4" style={{ alignItems: 'end' }}>
                        <div className="mform__field mform__field--span2">
                          <label className="mform__label">{isAr ? 'اسم المحور' : 'Axis name'}</label>
                          <input className="mform__input" value={ax.name} placeholder={t.phAxisName} onChange={(e) => { const axes = [...form.axes]; axes[xi] = { ...ax, name: e.target.value }; update({ axes }); }} />
                        </div>
                        <div className="mform__field">
                          <label className="mform__label">{isAr ? 'إدارة المحور' : 'Axis dept'}</label>
                          <DeptSelect value={ax.departmentId} options={scopedDepts} disabled={!form.branchId} onChange={(v) => { const axes = [...form.axes]; axes[xi] = { ...ax, departmentId: v }; update({ axes }); }} />
                        </div>
                        <div className="mform__field" style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                          <button type="button" className="btn btn-outline" onClick={() => addActivity(xi)}>{t.addAct}</button>
                          <button type="button" className="btn btn-ghost" style={{ color: '#c62828' }} onClick={() => update({ axes: form.axes.filter((_, i) => i !== xi) })}>{t.rem}</button>
                        </div>
                      </div>
                      <table className="op-mini" style={{ marginTop: 8 }}>
                        <thead>
                          <tr>
                            <th>{isAr ? 'النشاط' : 'Activity'}</th>
                            <th>{isAr ? 'التواريخ' : 'Dates'}</th>
                            <th>{isAr ? 'مسؤول' : 'Person'}</th>
                            <th>{isAr ? 'الإدارة' : 'Dept'}</th>
                            <th>%</th>
                            <th>{isAr ? 'الحالة' : 'Status'}</th>
                            <th>{t.rem}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {(ax.activities || []).length === 0 && (
                          <tr><td colSpan={7} className="op-plan__muted">{isAr ? 'لا أنشطة' : 'No activities'}</td></tr>
                          )}
                          {(ax.activities || []).map((act, ai) => (
                            <tr key={act._key}>
                              <td>
                                <input className="mform__input" style={{ marginBottom: 4 }} placeholder={t.phActivity} value={act.name} onChange={(e) => { const axes = [...form.axes]; const acts = [...axes[xi].activities]; acts[ai] = { ...act, name: e.target.value }; axes[xi] = { ...axes[xi], activities: acts }; update({ axes }); }} />
                                <input className="mform__input" placeholder={t.phActDesc} value={act.description} onChange={(e) => { const axes = [...form.axes]; const acts = [...axes[xi].activities]; acts[ai] = { ...act, description: e.target.value }; axes[xi] = { ...axes[xi], activities: acts }; update({ axes }); }} />
                              </td>
                              <td dir="ltr" style={{ minWidth: 110 }}>
                                <input type="date" className="mform__input" value={act.startDate} onChange={(e) => { const axes = [...form.axes]; const acts = [...axes[xi].activities]; acts[ai] = { ...act, startDate: e.target.value }; axes[xi] = { ...axes[xi], activities: acts }; update({ axes }); }} />
                                <input type="date" className="mform__input" style={{ marginTop: 4 }} value={act.endDate} onChange={(e) => { const axes = [...form.axes]; const acts = [...axes[xi].activities]; acts[ai] = { ...act, endDate: e.target.value }; axes[xi] = { ...axes[xi], activities: acts }; update({ axes }); }} />
                              </td>
                              <td><input className="mform__input" placeholder={t.phPerson} value={act.responsiblePersonName} onChange={(e) => { const axes = [...form.axes]; const acts = [...axes[xi].activities]; acts[ai] = { ...act, responsiblePersonName: e.target.value }; axes[xi] = { ...axes[xi], activities: acts }; update({ axes }); }} /></td>
                              <td style={{ minWidth: 140 }}><DeptSelect value={act.responsibleDepartmentId} options={scopedDepts} disabled={!form.branchId} onChange={(v) => { const axes = [...form.axes]; const acts = [...axes[xi].activities]; acts[ai] = { ...act, responsibleDepartmentId: v }; axes[xi] = { ...axes[xi], activities: acts }; update({ axes }); }} /></td>
                              <td style={{ width: 64 }}>
                                <input type="number" min={0} max={100} className="mform__input" value={act.progressPercentage} onChange={(e) => { const axes = [...form.axes]; const acts = [...axes[xi].activities]; acts[ai] = { ...act, progressPercentage: Number(e.target.value) || 0 }; axes[xi] = { ...axes[xi], activities: acts }; update({ axes }); }} />
                              </td>
                              <td>
                                <select className="mform__select" value={act.status} onChange={(e) => { const axes = [...form.axes]; const acts = [...axes[xi].activities]; acts[ai] = { ...act, status: e.target.value }; axes[xi] = { ...axes[xi], activities: acts }; update({ axes }); }}>
                                  {actStatuses.map((s) => <option key={s.v} value={s.v}>{s.l}</option>)}
                                </select>
                              </td>
                              <td><button type="button" className="btn btn-ghost" onClick={() => { const axes = [...form.axes]; const acts = axes[xi].activities.filter((_, j) => j !== ai); axes[xi] = { ...axes[xi], activities: acts }; update({ axes }); }}>{t.rem}</button></td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ))}
                </div>
              </div>

              <div className="op-plan__sec">
                <div className="op-plan__sec-head" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span>{t.hr}</span>
                  <button type="button" className="btn btn-outline" onClick={addHr}><Icon name="plus" size={12} /> {t.addRow}</button>
                </div>
                <div className="op-plan__sec-body">
                  <table className="op-mini">
                    <thead>
                      <tr>
                        <th>{isAr ? 'الدور' : 'Role'}</th>
                        <th>{isAr ? 'العدد' : '#'}</th>
                        <th>{isAr ? 'طبيعة العمل' : 'Nature'}</th>
                        <th>{isAr ? 'ملاحظات' : 'Notes'}</th>
                        <th>{t.rem}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {form.hrResources.map((row, ri) => (
                        <tr key={row._key}>
                          <td><input className="mform__input" placeholder={t.phHrRole} value={row.role} onChange={(e) => { const hr = [...form.hrResources]; hr[ri] = { ...row, role: e.target.value }; update({ hrResources: hr }); }} /></td>
                          <td style={{ width: 90 }}><input type="number" className="mform__input" placeholder={t.phHrNum} value={row.numberRequired} onChange={(e) => { const hr = [...form.hrResources]; hr[ri] = { ...row, numberRequired: e.target.value }; update({ hrResources: hr }); }} /></td>
                          <td><input className="mform__input" placeholder={t.phHrNature} value={row.natureOfWork} onChange={(e) => { const hr = [...form.hrResources]; hr[ri] = { ...row, natureOfWork: e.target.value }; update({ hrResources: hr }); }} /></td>
                          <td><input className="mform__input" placeholder={t.phHrNotes} value={row.notes} onChange={(e) => { const hr = [...form.hrResources]; hr[ri] = { ...row, notes: e.target.value }; update({ hrResources: hr }); }} /></td>
                          <td><button type="button" className="btn btn-ghost" onClick={() => update({ hrResources: form.hrResources.filter((_, i) => i !== ri) })}>{t.rem}</button></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="op-plan__sec">
                <div className="op-plan__sec-head" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span>{t.fin}</span>
                  <button type="button" className="btn btn-outline" onClick={addFin}><Icon name="plus" size={12} /> {t.addRow}</button>
                </div>
                <div className="op-plan__sec-body">
                  <table className="op-mini">
                    <thead>
                      <tr>
                        <th>{isAr ? 'البند' : 'Budget item'}</th>
                        <th>{isAr ? 'المقدر' : 'Est.'}</th>
                        <th>{isAr ? 'الفعلي' : 'Actual'}</th>
                        <th>{isAr ? 'ملاحظات' : 'Notes'}</th>
                        <th>{t.rem}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {form.financialResources.map((row, ri) => (
                        <tr key={row._key}>
                          <td><input className="mform__input" placeholder={t.phBudgetItem} value={row.budgetItem} onChange={(e) => { const xs = [...form.financialResources]; xs[ri] = { ...row, budgetItem: e.target.value }; update({ financialResources: xs }); }} /></td>
                          <td dir="ltr"><input className="mform__input" placeholder={t.phAmount} value={row.estimatedAmount} onChange={(e) => { const xs = [...form.financialResources]; xs[ri] = { ...row, estimatedAmount: e.target.value }; update({ financialResources: xs }); }} /></td>
                          <td dir="ltr"><input className="mform__input" placeholder={t.phAmount} value={row.actualAmount} onChange={(e) => { const xs = [...form.financialResources]; xs[ri] = { ...row, actualAmount: e.target.value }; update({ financialResources: xs }); }} /></td>
                          <td><input className="mform__input" placeholder={t.phFinNotes} value={row.notes} onChange={(e) => { const xs = [...form.financialResources]; xs[ri] = { ...row, notes: e.target.value }; update({ financialResources: xs }); }} /></td>
                          <td><button type="button" className="btn btn-ghost" onClick={() => update({ financialResources: form.financialResources.filter((_, i) => i !== ri) })}>{t.rem}</button></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="op-plan__sec">
                <div className="op-plan__sec-head" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span>{t.kpiSec}</span>
                  <button type="button" className="btn btn-outline" onClick={addKpi}><Icon name="plus" size={12} /> {t.addRow}</button>
                </div>
                <div className="op-plan__sec-body">
                  <table className="op-mini">
                    <thead>
                      <tr>
                        <th>{isAr ? 'مؤشر' : 'KPI'}</th>
                        <th>{isAr ? 'هدف ذو صلة' : 'Objective'}</th>
                        <th>{isAr ? 'المستهدف' : 'Target'}</th>
                        <th>{isAr ? 'الإدارة' : 'Dept'}</th>
                        <th>{isAr ? 'القياس' : 'Measurement'}</th>
                        <th>Q1/Q2/Q3</th>
                        <th>{t.rem}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {form.kpis.map((row, ri) => (
                        <tr key={row._key}>
                          <td><input className="mform__input" placeholder={t.phKpiName} value={row.name} onChange={(e) => { const kp = [...form.kpis]; kp[ri] = { ...row, name: e.target.value }; update({ kpis: kp }); }} /></td>
                          <td>
                            <select className="mform__select" value={row.relatedObjectiveSortOrder} onChange={(e) => { const kp = [...form.kpis]; kp[ri] = { ...row, relatedObjectiveSortOrder: e.target.value }; update({ kpis: kp }); }}>
                              <option value="">{isAr ? 'بدون' : '—'}</option>
                              {form.objectives.filter((o) => (o.objectiveText || '').trim()).map((o, oIdx) => (
                                <option key={o._key} value={String(oIdx)}>{(o.objectiveText || '').slice(0, 80)}{(o.objectiveText || '').length > 80 ? '…' : ''}</option>
                              ))}
                            </select>
                          </td>
                          <td><input className="mform__input" placeholder={t.phKpiTarget} value={row.targetValue} onChange={(e) => { const kp = [...form.kpis]; kp[ri] = { ...row, targetValue: e.target.value }; update({ kpis: kp }); }} /></td>
                          <td style={{ minWidth: 140 }}><DeptSelect value={row.responsibleDepartmentId} options={scopedDepts} disabled={!form.branchId} onChange={(v) => { const kp = [...form.kpis]; kp[ri] = { ...row, responsibleDepartmentId: v }; update({ kpis: kp }); }} /></td>
                          <td><input className="mform__input" placeholder={t.phKpiMeas} value={row.measurementMethod} onChange={(e) => { const kp = [...form.kpis]; kp[ri] = { ...row, measurementMethod: e.target.value }; update({ kpis: kp }); }} /></td>
                          <td dir="ltr" style={{ minWidth: 140 }}>
                            <input className="mform__input" placeholder="Q1" value={row.quarter1} onChange={(e) => { const kp = [...form.kpis]; kp[ri] = { ...row, quarter1: e.target.value }; update({ kpis: kp }); }} style={{ marginBottom: 4 }} />
                            <input className="mform__input" placeholder="Q2" value={row.quarter2} onChange={(e) => { const kp = [...form.kpis]; kp[ri] = { ...row, quarter2: e.target.value }; update({ kpis: kp }); }} style={{ marginBottom: 4 }} />
                            <input className="mform__input" placeholder="Q3" value={row.quarter3} onChange={(e) => { const kp = [...form.kpis]; kp[ri] = { ...row, quarter3: e.target.value }; update({ kpis: kp }); }} />
                          </td>
                          <td><button type="button" className="btn btn-ghost" onClick={() => update({ kpis: form.kpis.filter((_, i) => i !== ri) })}>{t.rem}</button></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="op-plan__sec">
                <div className="op-plan__sec-head" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span>{t.riskSec}</span>
                  <button type="button" className="btn btn-outline" onClick={addRisk}><Icon name="plus" size={12} /> {t.addRow}</button>
                </div>
                <div className="op-plan__sec-body">
                  <table className="op-mini">
                    <thead>
                      <tr>
                        <th>{isAr ? 'الوصف' : 'Risk'}</th>
                        <th>{isAr ? 'احتمالية' : 'Prob.'}</th>
                        <th>{isAr ? 'التأثير' : 'Impact'}</th>
                        <th>{isAr ? 'التخفيف' : 'Mitigation'}</th>
                        <th>{t.rem}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {form.risks.map((row, ri) => (
                        <tr key={row._key}>
                          <td><input className="mform__input" placeholder={t.phRiskDesc} value={row.description} onChange={(e) => { const rk = [...form.risks]; rk[ri] = { ...row, description: e.target.value }; update({ risks: rk }); }} /></td>
                          <td><input className="mform__input" placeholder={t.phRiskProb} value={row.probability} onChange={(e) => { const rk = [...form.risks]; rk[ri] = { ...row, probability: e.target.value }; update({ risks: rk }); }} /></td>
                          <td><input className="mform__input" placeholder={t.phRiskImpact} value={row.impact} onChange={(e) => { const rk = [...form.risks]; rk[ri] = { ...row, impact: e.target.value }; update({ risks: rk }); }} /></td>
                          <td><input className="mform__input" placeholder={t.phRiskMit} value={row.mitigationPlan} onChange={(e) => { const rk = [...form.risks]; rk[ri] = { ...row, mitigationPlan: e.target.value }; update({ risks: rk }); }} /></td>
                          <td><button type="button" className="btn btn-ghost" onClick={() => update({ risks: form.risks.filter((_, i) => i !== ri) })}>{t.rem}</button></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="op-plan__sec">
                <div className="op-plan__sec-head">{t.appr}</div>
                <div className="op-plan__sec-body mform">
                  <div className="mform__row mform__row--4">
                    <div className="mform__field mform__field--span2">
                      <label className="mform__label">{t.lblRev}</label>
                      <input className="mform__input" value={form.reviewedBy} onChange={(e) => update({ reviewedBy: e.target.value })} placeholder={t.phRev} />
                    </div>
                  </div>
                  <label className="field-label" style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 8 }}>
                    <input type="checkbox" checked={form.approvalConfirmed} onChange={(e) => update({ approvalConfirmed: e.target.checked })} />
                    {t.lblConfirmed}
                  </label>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
